/*
 * The POST /v2/sales_task_list/generation_config body's `rules` map, built from
 * the Task Generation card's state.
 *
 * Its own module, and pure, because this is where the config UI silently loses
 * fields: it builds a fresh literal per rule rather than sending the rule object
 * back, so a field added to the card and not added here is accepted by the
 * inputs, marked dirty, saved — and discarded, with no error anywhere. That is
 * exactly what the instant-tier fields would have hit, and it is why this is a
 * tested function rather than an inline loop in the page component.
 */

// A blank numeric rule field, as an integer or null. '' / null / undefined /
// unparseable all mean "the operator left the box empty"; 0 is a real value and
// survives (it disables the dedup window).
// A Task Generation card rule: goserver's RuleConfig JSON, with the numeric
// boxes holding whatever the input last produced.
type NumericInput = number | string | null | undefined;

export interface GenRule {
  task_type: string;
  enabled?: boolean;
  enabled_only?: boolean;
  is_instant?: boolean;
  expiry_minutes?: NumericInput;
  dedup_window_hours?: NumericInput;
  grace_minutes?: NumericInput;
  has_grace?: boolean;
  delay_minutes?: NumericInput;
  has_delay?: boolean;
  days?: number[];
  has_days?: boolean;
  refresh_rate_hours?: NumericInput;
  due_offset_hours?: NumericInput;
  enabled_at?: string;
}

// One entry of the POST body's `rules` map (goserver RuleConfig).
export interface GenRulePayload {
  enabled: boolean;
  is_instant: boolean;
  expiry_minutes?: number;
  dedup_window_hours?: number;
  grace_minutes?: number;
  delay_minutes?: number;
  days?: number[];
  refresh_rate_hours?: number;
  due_offset_hours?: number;
}

const optionalInt = (raw: NumericInput): number | null => {
  if (raw === '' || raw === null || raw === undefined) return null;
  const n = parseInt(String(raw), 10);
  return Number.isNaN(n) ? null : n;
};

/*
 * Two omissions are deliberate, not oversights:
 *  - expiry_minutes is omitted when blank OR 0. The server reads an absent field
 *    as "never expires" and clamps a present one up to its 1-minute minimum, so
 *    sending 0 would turn "no expiry" into a one-minute expiry. (The GET emits 0
 *    for no-expiry, which is why 0 must be treated as blank here.)
 *  - dedup_window_hours is omitted only when blank, so the server applies its own
 *    default; an explicit 0 is sent, because 0 means "no window" and must stay
 *    distinguishable from unset.
 *  - grace_minutes is sent only for the rules that declare has_grace, and only
 *    when the box is non-blank; an explicit 0 IS sent, because MinGraceMinutes
 *    is 0 and "create the task immediately" is a real setting. Sending it for a
 *    rule without has_grace would write a knob nothing reads; omitting it for a
 *    rule that has one wipes the stored value on every save.
 *  - delay_minutes is keyed on has_delay: it is the wait before the delayed
 *    re-check that decides whether the friction resolved itself. It is omitted
 *    when blank OR 0, like expiry and unlike grace: MinDelayMinutes is 1, so a
 *    0 is not rejected but clamped up to a one-minute delay — "no opinion" must
 *    round-trip as absent, letting the rule's own default apply. A server that
 *    does not send has_delay yet gets no key at all.
 *
 * enabled_at (server-stamped, read-only) is never sent.
 *
 * Enabled-only rules (Fresh lead, Upcoming trial session) still send no schedule
 * — an event or their own sweep is the trigger — but they DO send the
 * instant-tier fields: instant is orthogonal to the day-offset schedule, and
 * these event-driven rules are the ones most likely to be flipped instant.
 */
const buildGenConfigPayload = (
  genRules: Array<GenRule | null | undefined> | null | undefined
): Record<string, GenRulePayload> => {
  const rules: Record<string, GenRulePayload> = {};
  (genRules || []).forEach((r) => {
    if (!r || !r.task_type) return;
    const rule: GenRulePayload = { enabled: !!r.enabled, is_instant: !!r.is_instant };

    const expiry = optionalInt(r.expiry_minutes);
    if (expiry !== null && expiry !== 0) rule.expiry_minutes = expiry;

    const dedup = optionalInt(r.dedup_window_hours);
    if (dedup !== null) rule.dedup_window_hours = dedup;

    if (r.has_grace) {
      const grace = optionalInt(r.grace_minutes);
      if (grace !== null) rule.grace_minutes = grace;
    }

    if (r.has_delay) {
      const delay = optionalInt(r.delay_minutes);
      if (delay !== null && delay !== 0) rule.delay_minutes = delay;
    }

    if (!r.enabled_only) {
      // has_days === false means the rule's generator never reads days (it has
      // its own re-scan window); absent means true, so nothing changes for a
      // server that does not send the flag yet.
      if (r.has_days !== false) rule.days = r.days || [];
      rule.refresh_rate_hours = Number(r.refresh_rate_hours) || 0;
      rule.due_offset_hours = Number(r.due_offset_hours) || 0;
    }
    rules[r.task_type] = rule;
  });
  return rules;
};

export default buildGenConfigPayload;
